import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Calendar, User, ArrowLeft } from "lucide-react";
import styled from "styled-components";
import apiConfig from "../../config/apiConfig";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || "#6b7280"};
  text-decoration: none;
  margin-bottom: 24px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }

  &:hover {
    text-decoration: underline;
  }
`;

const PostTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 32px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const PostBody = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.body};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#e5e7eb"};
  }

  & p {
    margin: 0 0 1em 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }
`;

function formatBlogDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function BlogPost() {
  const { idOrSlug } = useParams();
  const { pathname } = useLocation();
  const lang = useSelector((state) => state.translation?.currentLanguage) || "en";
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const langPrefix = pathname.match(/^\/[a-z]{2}(-[a-z0-9]+)?(\/|$)/i)?.[0]?.replace(/\/$/, "") || "";
  const blogListPath = langPrefix ? `${langPrefix}/blog` : "/blog";

  useEffect(() => {
    if (!idOrSlug) {
      setLoading(false);
      return;
    }
    const base = apiConfig.BASE_URL || "";
    const qs = lang && lang !== "en" ? `?lang=${encodeURIComponent(lang)}` : "";
    setLoading(true);
    setError(false);
    fetch(`${base}/api/blog/${encodeURIComponent(idOrSlug)}${qs}`)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then((data) => {
        setPost(data);
      })
      .catch(() => {
        setPost(null);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [idOrSlug, lang]);

  if (loading) {
    return (
      <Layout>
        <PageTransition>
          <Section>
            <Container>
              <p style={{ color: "var(--color-muted, #6b7280)" }}>Loading…</p>
            </Container>
          </Section>
        </PageTransition>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <PageTransition>
          <Section>
            <Container>
              <BackLink to={blogListPath}>
                <ArrowLeft size={16} />
                Back to Blog
              </BackLink>
              <p style={{ color: "var(--color-muted, #6b7280)" }}>Post not found.</p>
            </Container>
          </Section>
        </PageTransition>
      </Layout>
    );
  }

  const seoTitle = post.meta?.title || post.title;
  const seoDescription = post.meta?.description || post.excerpt || "";

  return (
    <Layout>
      <SEO title={seoTitle} description={seoDescription} hreflang />
      <PageTransition>
        <Section>
          <Container>
            <BackLink to={blogListPath}>
              <ArrowLeft size={16} />
              Back to Blog
            </BackLink>
            <PostTitle>{post.title}</PostTitle>
            <PostMeta>
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Calendar size={16} />
                {formatBlogDate(post.publishedAt || post.createdAt)}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <User size={16} />
                {post.author || "Kliq CRM"}
              </span>
            </PostMeta>
            <PostBody>
              {post.body ? post.body.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>) : <p>{post.excerpt || ""}</p>}
            </PostBody>
          </Container>
        </Section>
      </PageTransition>
    </Layout>
  );
}
